import type * as React from 'react';
import { Button } from '@doda/io-react/common';
import { apiClient } from '@doda/api-client/util/apiClient';
import type {
  JobSearchResultResponse,
  ButtonPosition,
} from '@doda/api-client/aspida/@types';
import {
  BUTTON_POSITION,
  ENTRY_ID,
} from '@doda/common/src/constants/clicksCountUp';
import { createApplyingLink } from '../../logics/createApplyingLink';
import styles from './index.module.scss';

/**
 * CommonButtonProps インターフェース
 * 共通のボタンプロパティ
 */
interface CommonButtonProps {
  /** ボタンがクリックされたときのハンドラー */
  onApply: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * DirectButton コンポーネント
 * 直接応募ボタン
 * @param {CommonButtonProps} props - プロパティ
 * @returns {JSX.Element}
 */
export const DirectButton = ({ onApply }: CommonButtonProps) => {
  return (
    <div style={{ width: 150 }}>
      <Button variant="blue" size="S" widthPercentage={100} onClick={onApply}>
        応募する
      </Button>
    </div>
  );
};

/**
 * AgentButton コンポーネント
 * エージェント応募依頼ボタン
 * @param {CommonButtonProps} props - プロパティ
 * @returns {JSX.Element}
 */
export const AgentButton = ({ onApply }: CommonButtonProps) => {
  return (
    <div style={{ width: 150 }}>
      <Button variant="orange" size="S" widthPercentage={100} onClick={onApply}>
        <span>
          応募依頼する
          <span className={styles['jobCard-applyButton__subText']}>
            （エージェントサービス）
          </span>
        </span>
      </Button>
    </div>
  );
};

/**
 * ApplyEventHandler 型
 * 応募イベントハンドラー
 */
export type ApplyEventHandler = CommonButtonProps['onApply'];

/**
 * ApplyButtonProps インターフェース
 * ApplyButtonのプロパティ
 */
export interface ApplyButtonProps {
  /** 求人の種類 */
  jobOfferKind: NonNullable<JobSearchResultResponse['jobOfferKind']>;
  /** 求人ID */
  jid: NonNullable<JobSearchResultResponse['jid']>;
  /** 直接応募のイベントハンドラー */
  onApplyDirect?: ApplyEventHandler;
  /** エージェント応募のイベントハンドラー */
  onApplyAgent?: ApplyEventHandler;
  /** ダイレクトリンクを持っているか */
  hasDirectLink: JobSearchResultResponse['hasDirectLink'];
  /** ダイレクトリンクURL */
  directLink: JobSearchResultResponse['directLink'];
}

/**
 * ApplyButton コンポーネント
 * 応募ボタン
 * @param {ApplyButtonProps} props - プロパティ
 * @returns {JSX.Element}
 */
export const ApplyButton = ({
  jobOfferKind,
  jid,
  onApplyDirect,
  onApplyAgent,
  hasDirectLink = false,
  directLink,
}: ApplyButtonProps) => {
  const isAgent = jobOfferKind === '0';
  const isDirect = jobOfferKind === '2';
  /**
   * エージェント応募イベントのハンドラー
   * @param {React.MouseEvent<HTMLButtonElement>} e - イベントオブジェクト
   */
  const handleApplyAgent: ApplyEventHandler = (e) => {
    if (onApplyAgent) onApplyAgent(e);
    const path = createApplyingLink('Agent', jid);
    window.open(path, jid);
  };

  /**
   * 直接応募イベントのハンドラー
   * @param {React.MouseEvent<HTMLButtonElement>} e - イベントオブジェクト
   */
  const handleApplyDirect: ApplyEventHandler = async (e) => {
    if (onApplyDirect) onApplyDirect(e);
    if (hasDirectLink && directLink) {
      await apiClient.api.v1.job._jid(jid).clicksCountUp.$post({
        body: {
          buttonPosition: BUTTON_POSITION.list as ButtonPosition,
          entryId: ENTRY_ID.direct,
          device: 'PC',
        },
      });
      window.open(directLink, jid, 'noreferrer');
      return;
    }
    const path = createApplyingLink('Direct', jid);
    window.open(path, jid);
  };

  /**
   * 応募イベントのハンドラー
   * @param {React.MouseEvent<HTMLButtonElement>} e - イベントオブジェクト
   */
  const handleApply: ApplyEventHandler = (e) => {
    if (isAgent) {
      handleApplyAgent(e);
      return;
    }
    handleApplyDirect(e);
  };

  if (isAgent) return <AgentButton onApply={handleApply} />;
  if (isDirect) return <DirectButton onApply={handleApply} />;
  return null;
};
